import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, Star, Filter, Sparkles, Navigation, LayoutGrid, Map as MapIcon } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import VerifiedBadge from "@/components/VerifiedBadge";
import VoiceSearchButton from "@/components/VoiceSearchButton";
import ListingsMap from "@/components/ListingsMap";
import { useAuth, ROLE_THEMES } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";
import { formatPriceText } from "@/lib/format";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1560493676-04071c5f467b?auto=format&fit=crop&w=800&q=80";

export default function Marketplace() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const theme = user ? ROLE_THEMES[user.role] : ROLE_THEMES.farmer;

  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(params.get("category") || "All");
  const [state, setStateFilter] = useState("");
  const [maxKm, setMaxKm] = useState(0); // 0 = unlimited
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid"); // grid | map

  // Reset stale category filter whenever the active role changes — a Farmer
  // category like "Vegetables" would return 0 results on the Day Worker
  // marketplace and vice-versa. This was the root cause of the M791 report.
  useEffect(() => {
    if (!user) return;
    const roleCats = ROLE_THEMES[user.role]?.categories || [];
    if (cat !== "All" && !roleCats.includes(cat)) {
      setCat("All");
      setParams({});
    }
  }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const getMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    toast.loading("Getting your location...", { id: "loc" });
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); toast.success("Location set", { id: "loc" }); if (maxKm === 0) setMaxKm(10); },
      (err) => { toast.error(err.code === 1 ? "Permission denied. Enable location in browser." : "Could not get location.", { id: "loc" }); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  useEffect(() => {
    setLoading(true);
    const query = {
      search: q || undefined,
      category: cat !== "All" ? cat : undefined,
      state: state || undefined,
      lat: coords.lat ?? undefined,
      lng: coords.lng ?? undefined,
      max_km: maxKm > 0 ? maxKm : undefined,
    };
    api.get("/listings", { params: query })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [q, cat, state, coords, maxKm]);

  const categories = useMemo(() => ["All", ...theme.categories], [theme]);

  return (
    <AppShell>
      <div className="px-4 md:px-8 lg:px-12 py-8">
        <div className="flex items-baseline justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-gray-500">{t("market.title")}</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">{t("market.for")} {theme.name}s</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="inline-flex bg-white border border-gray-200 rounded-full p-1">
              {[{ k: "grid", i: LayoutGrid, l: "Grid" }, { k: "map", i: MapIcon, l: "Map" }].map((v) => (
                <button key={v.k} data-testid={`view-${v.k}`} onClick={() => setView(v.k)}
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm ${view === v.k ? `${theme.bg} text-white` : "text-gray-600"}`}>
                  <v.i className="h-3.5 w-3.5" />{v.l}
                </button>
              ))}
            </div>
            <div className={`text-xs uppercase tracking-wider ${theme.text}`}>{t("market.role_locked")}</div>
          </div>
        </div>

        <div className="mt-6 flex gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px] flex items-center gap-2 bg-white rounded-full border border-gray-200 px-5 py-3 premium-shadow">
            <Search className="h-4 w-4 text-gray-400" />
            <input data-testid="marketplace-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("market.search")}
              className="flex-1 outline-none bg-transparent text-sm" />
            <VoiceSearchButton onResult={(txt) => setQ(txt)} />
          </div>
          <div className="flex items-center gap-2 bg-white rounded-full border border-gray-200 px-5 py-3 premium-shadow">
            <Filter className="h-4 w-4 text-gray-400" />
            <select data-testid="marketplace-category" value={cat} onChange={(e) => { setCat(e.target.value); setParams(e.target.value === "All" ? {} : { category: e.target.value }); }}
              className="bg-transparent outline-none text-sm pr-4">
              {categories.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-full border border-gray-200 px-4 py-3 premium-shadow">
            <MapPin className="h-4 w-4 text-gray-400" />
            <input data-testid="marketplace-state" value={state} onChange={(e) => setStateFilter(e.target.value)} placeholder="State" className="w-24 outline-none bg-transparent text-sm" />
          </div>
        </div>

        {/* Distance controls */}
        <div className="mt-4 flex items-center gap-4 flex-wrap rounded-2xl bg-white border border-gray-100 px-5 py-3 premium-shadow">
          <button data-testid="use-my-location" onClick={getMyLocation} className={`inline-flex items-center gap-1.5 text-sm font-semibold ${coords.lat ? "text-emerald-700" : "text-gray-700 hover:text-emerald-700"}`}>
            <Navigation className="h-4 w-4" />{coords.lat ? "Location on" : "Use my location"}
          </button>
          <div className="flex-1 min-w-[240px] flex items-center gap-3">
            <label className="text-xs uppercase tracking-widest text-gray-500 whitespace-nowrap">Distance</label>
            <input data-testid="distance-slider" type="range" min="0" max="500" step="5" value={maxKm} onChange={(e) => setMaxKm(parseInt(e.target.value))}
              className="flex-1 accent-emerald-600" disabled={!coords.lat} />
            <div className={`text-xs font-semibold ${coords.lat ? "text-emerald-700" : "text-gray-400"}`}>{maxKm === 0 ? "Any" : `${maxKm} km`}</div>
          </div>
          {[5, 10, 100, 200, 500].map((v) => (
            <button key={v} data-testid={`km-${v}`} onClick={() => setMaxKm(v)} disabled={!coords.lat}
              className={`text-xs px-3 py-1.5 rounded-full ${maxKm === v ? theme.bg + " text-white" : "bg-gray-50 text-gray-600"} disabled:opacity-40`}>{v} km</button>
          ))}
          {!coords.lat && (
            <div className="w-full text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-1">
              Tap <b>Use my location</b> first to enable distance filters. Make sure location permission is granted in your browser.
            </div>
          )}
        </div>

        {/* Categories chips */}
        <div className="mt-6 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button key={c} onClick={() => { setCat(c); setParams(c === "All" ? {} : { category: c }); }}
              className={`px-4 py-2 rounded-full text-sm border ${cat === c ? `${theme.bg} text-white border-transparent` : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"}`}>
              {c}
            </button>
          ))}
        </div>

        {/* Listings grid */}
        <div className="mt-8">
          {loading ? (
            <div className="text-center py-24 text-gray-500">Loading...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-24 rounded-3xl bg-white border border-gray-100">
              <div className="text-4xl">{coords.lat && maxKm > 0 ? "📍" : "🌾"}</div>
              <div className="mt-2 text-xl font-semibold text-gray-900">{coords.lat && maxKm > 0 ? "No nearby results" : "No listings yet"}</div>
              <div className="text-sm text-gray-500 mt-1">{coords.lat && maxKm > 0 ? `Nothing within ${maxKm} km. Try a larger radius or clear the location filter.` : "Be the first to post in your role's marketplace."}</div>
              {coords.lat && maxKm > 0 ? (
                <button data-testid="clear-distance" onClick={() => setMaxKm(0)} className={`mt-6 px-6 py-3 rounded-full text-white ${theme.bg}`}>Clear distance filter</button>
              ) : (
              <button data-testid="empty-post-btn" onClick={() => nav("/post")} className={`mt-6 px-6 py-3 rounded-full text-white ${theme.bg}`}>Post something</button>
              )}
            </div>
          ) : view === "map" ? (
            <>
              <ListingsMap items={items} role={user?.role} userLoc={coords} height={520} />
              <div className="mt-3 text-xs text-gray-500">
                Showing {items.filter((i) => i.lat && i.lng).length} of {items.length} listings on map. Listings without coordinates aren't pinned.
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {items.map((it, idx) => (
                <motion.div key={it.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}
                  data-testid={`listing-card-${it.id}`}
                  onClick={() => nav(`/marketplace/${it.id}`)}
                  className="cursor-pointer group rounded-3xl bg-white overflow-hidden border border-gray-100 premium-shadow hover:-translate-y-1 transition-all">
                  <div className="relative aspect-[4/3] overflow-hidden bg-gray-50 flex items-center justify-center">
                    <img src={it.images?.[0] || FALLBACK_IMG} alt={it.name} loading="lazy" className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                    <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest ${theme.bg} text-white`}>{it.kind}</div>
                    {it.is_featured && (
                      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest bg-amber-500 text-white flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />{t("listing.featured")}
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold text-gray-900 line-clamp-1">{it.name}</div>
                        <div className="text-xs text-gray-500 mt-1">{it.category}</div>
                      </div>
                      <div className={`text-lg font-bold ${theme.text}`}>{formatPriceText(it)}</div>
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                      <MapPin className="h-3 w-3" />
                      <span className="line-clamp-1">{[it.village, it.city, it.state].filter(Boolean).join(", ") || it.location || "—"}</span>
                      {it.distance_km != null && <span className={`ml-1 text-emerald-700 font-semibold`}>· {it.distance_km} km</span>}
                      <span className="ml-auto flex items-center gap-1 text-amber-500"><Star className="h-3 w-3 fill-amber-500" />{it.avg_rating ?? "—"}</span>
                    </div>
                    <div className="mt-3 text-xs text-gray-500 flex items-center gap-1">By <b className="text-gray-800">{it.owner_name}</b>{it.owner_verified && <VerifiedBadge small />}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
